/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * cloudUpload — Direct-to-R2 multipart uploads (Stage 2 / Phase A).
 *
 * Drop-in replacement for `chunkedUpload.js`'s public surface so
 * `LessonReview` and `ClipBasketModal` can swap by import alone:
 *
 *   await cloudUpload({
 *     file, purpose, context,
 *     completeUrl: `/api/lesson-review/cloud-complete`,
 *     completeBody: {},
 *     onProgress, signal, resumeUploadId,
 *   });
 *
 * Bytes flow browser → R2 directly via signed PUT URLs. The FastAPI
 * backend's role is purely orchestration:
 *   - `/init` returns upload_id + signed PUT URL per part.
 *   - PUT each part to R2; capture the `ETag` response header.
 *   - POST {part_number, etag} back to `/{id}/part-complete`.
 *   - When all parts uploaded, POST `/{id}/complete` → backend calls
 *     R2's `complete_multipart_upload` and returns the canonical key.
 *   - Caller's `completeUrl` then handles purpose-specific finalize
 *     (e.g., creating the lesson_review record).
 *
 * INVARIANTS PRESERVED FROM STAGE 1 (chunkedUpload.js):
 *   • One source of truth: server `/status.received_parts`.
 *   • Resume seeds from server truth; no optimistic local counters.
 *   • Parallel workers (3) gate progress on PUT-resolved ETag.
 *   • `received` Set + `sentBytes` are SINGLE shared accumulators
 *     across the worker pool — no shadowing.
 *   • Throttled progress (500ms), stage transitions flush immediately.
 *   • Per-chunk retry (3 attempts, exponential backoff + jitter).
 *   • UploadPausedError / AuthExpiredError typed exits with full
 *     resume metadata.
 *   • console.warn on every chunk failure with the same diagnostic
 *     vocabulary as Stage 1.
 *
 * NEW vs Stage 1:
 *   • Each PUT goes to a CDN URL (signed PUT). No Authorization
 *     header — the signature carries auth. axios `withCredentials`
 *     is OFF for these (cross-origin to *.r2.cloudflarestorage.com).
 *   • Backend never touches bytes. /status returns fresh signed URLs
 *     for any pending parts (1-hour TTL, automatic re-mint on resume).
 *   • Part numbers are 1-indexed (S3 multipart convention) — index
 *     conversion happens at the boundary; internal received Set is
 *     1-indexed throughout.
 */
import axios from "axios";
// Re-use Stage 1's typed errors so consumers can `instanceof`-check
// against either upload backend. Class identity matters for instanceof.
import {
  AuthExpiredError as Stage1AuthExpiredError,
  UploadPausedError as Stage1UploadPausedError,
} from "./chunkedUpload";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_RETRIES_PER_CHUNK = 3;
const PARALLEL_CHUNKS = 3;
const PROGRESS_THROTTLE_MS = 500;

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

// Re-export the SAME classes Stage 1 uses so `instanceof` checks in
// LessonReview / ClipBasketModal work regardless of which library
// threw the error.
export const AuthExpiredError = Stage1AuthExpiredError;
export const UploadPausedError = Stage1UploadPausedError;

/**
 * CloudConfigError — thrown when the backend `/api/uploads/cloud/init`
 * returns HTTP 503 "Cloud uploads not configured" (R2 env vars missing
 * on the host). This is a TERMINAL deployment-time condition, NOT a
 * retryable transient. Callers should surface a clear message and
 * stop. R2-native option C (May 24, 2026): no silent fallback to the
 * local-relay path in preview — operator must see "Cloud upload is
 * not configured in preview" verbatim.
 */
export class CloudConfigError extends Error {
  constructor(detail) {
    super("Cloud upload is not configured in preview");
    this.name = "CloudConfigError";
    this.serverDetail = detail || null;
  }
}


async function tryRefreshSession() {
  try {
    await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
    return true;
  } catch {
    return false;
  }
}


export async function cloudUpload({
  file,
  purpose,
  context = {},
  completeUrl,
  completeBody = {},
  onProgress,
  signal,
  resumeUploadId,
}) {
  if (!file) throw new Error("No file");
  if (!purpose) throw new Error("Missing purpose");
  if (!completeUrl) throw new Error("Missing completeUrl");

  // ── 1. Init / rehydrate session ─────────────────────────────────
  let upload_id;
  let chunk_size;
  let total_parts;
  let signed_urls = {};        // { partNumber: url }
  const received = new Set();  // 1-indexed part numbers
  let sentBytes = 0;

  if (resumeUploadId) {
    let statusRes;
    try {
      statusRes = await axios.get(
        `${API}/api/uploads/cloud/${resumeUploadId}/status`,
        { withCredentials: true, signal },
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      throw new Error(`Resume failed: ${detail || "couldn't reach upload session"}`);
    }
    const s = statusRes.data || {};
    upload_id = resumeUploadId;
    chunk_size = s.chunk_size;
    total_parts = s.total_parts;
    for (const i of s.received_parts || []) received.add(i);
    signed_urls = s.signed_part_urls || {};
    // Recompute sentBytes from confirmed parts.
    for (const i of received) {
      const start = (i - 1) * chunk_size;
      const end = Math.min(start + chunk_size, file.size);
      sentBytes += Math.max(0, end - start);
    }
    if (typeof console !== "undefined" && console.info) {
      const nextIdx = (() => {
        for (let n = 1; n <= total_parts; n++) if (!received.has(n)) return n;
        return total_parts + 1;
      })();
      const seedPct = file.size ? Math.round((sentBytes / file.size) * 100) : 0;
      console.info(
        `[cloudUpload] resuming uploadId=${upload_id} ` +
        `receivedChunks=${received.size}/${total_parts} ` +
        `nextChunkIndex=${nextIdx - 1} ` +     // 0-indexed in log
        `displayedPercent=${seedPct}%`,
      );
    }
  } else {
    let initRes;
    try {
      initRes = await axios.post(
        `${API}/api/uploads/cloud/init`,
        {
          filename: file.name,
          total_bytes: file.size,
          purpose,
          context,
          mime_type: file.type || null,
        },
        { withCredentials: true, signal },
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const text = (typeof detail === "string" && detail)
        || detail?.message || err?.message || "Init failed";
      // HTTP 503 = R2 env not configured on the host. Throw the typed
      // error so the caller (ClipBasketV2) can show the operator-
      // locked "Cloud upload is not configured in preview" message
      // and STOP — no silent fallback to local relay.
      if (status === 503) {
        throw new CloudConfigError(text);
      }
      // Tagged with `cloud_init_failed:` so uploadAdapter can
      // recognise this as an infra-level failure and transparently
      // fall back to Stage 1 (no bytes sent yet — safe to retry).
      throw new Error("cloud_init_failed: " + text);
    }
    upload_id = initRes.data.upload_id;
    chunk_size = initRes.data.chunk_size;
    total_parts = initRes.data.total_parts;
    signed_urls = initRes.data.signed_part_urls || {};
  }

  // ── Throttled progress ──────────────────────────────────────────
  let _lastProgressAt = 0;
  let _pendingProgressTimer = null;
  let _lastStage = "uploading";
  const _completionTimestamps = [];
  let _retryCount = 0;

  function _recordChunkCompletion(bytes) {
    _completionTimestamps.push({ at: Date.now(), bytes });
    if (_completionTimestamps.length > 30) _completionTimestamps.shift();
  }
  function _currentMbps() {
    if (_completionTimestamps.length < 2) return null;
    const first = _completionTimestamps[0];
    const last = _completionTimestamps[_completionTimestamps.length - 1];
    const dtSec = Math.max(0.001, (last.at - first.at) / 1000);
    const totalBytes = _completionTimestamps.reduce((a, c) => a + c.bytes, 0);
    return (totalBytes * 8) / 1_000_000 / dtSec;
  }
  function _flushProgress(stage) {
    if (typeof onProgress !== "function") return;
    const pct = Math.min(100, Math.round((sentBytes / file.size) * 100));
    let lastSuccessful = -1;
    for (const i of received) if (i - 1 > lastSuccessful) lastSuccessful = i - 1;
    onProgress({
      pct,
      sent: sentBytes,
      total: file.size,
      current_chunk: received.size,
      total_chunks: total_parts,
      last_successful_chunk: lastSuccessful,
      upload_id,
      stage,
      mbps: _currentMbps(),
    });
    _lastProgressAt = Date.now();
  }
  const reportProgress = (stage = "uploading") => {
    const stageChanged = stage !== _lastStage;
    _lastStage = stage;
    if (stageChanged || Date.now() - _lastProgressAt >= PROGRESS_THROTTLE_MS) {
      if (_pendingProgressTimer) {
        clearTimeout(_pendingProgressTimer);
        _pendingProgressTimer = null;
      }
      _flushProgress(stage);
      return;
    }
    if (_pendingProgressTimer) return;
    _pendingProgressTimer = setTimeout(() => {
      _pendingProgressTimer = null;
      _flushProgress(_lastStage);
    }, PROGRESS_THROTTLE_MS);
  };
  reportProgress();

  // ── Re-mint a single signed URL via /status (handles 1-hour expiry) ──
  async function _remintSignedUrl(partNumber) {
    try {
      const r = await axios.get(
        `${API}/api/uploads/cloud/${upload_id}/status`,
        { withCredentials: true, signal },
      );
      const fresh = r.data?.signed_part_urls || {};
      if (fresh[partNumber]) {
        signed_urls[partNumber] = fresh[partNumber];
        return fresh[partNumber];
      }
    } catch { /* ignore */ }
    return null;
  }

  // ── Per-part upload helper ──────────────────────────────────────
  async function uploadOnePart(partNumber) {
    const start = (partNumber - 1) * chunk_size;
    const end = Math.min(start + chunk_size, file.size);
    const blob = file.slice(start, end);

    let lastErr = null;
    for (let attempt = 0; attempt < MAX_RETRIES_PER_CHUNK; attempt++) {
      if (signal?.aborted) throw new Error("Upload cancelled");

      let url = signed_urls[partNumber];
      if (!url) {
        url = await _remintSignedUrl(partNumber);
        if (!url) {
          // /status couldn't mint — treat as transient.
          lastErr = new Error("missing signed url");
          await sleep(500);
          continue;
        }
      }

      // PUT directly to R2. No `withCredentials` (cross-origin), no
      // Content-Type header (R2 multipart parts are octet-stream and
      // setting it would change the canonical signed request).
      let putRes;
      try {
        putRes = await axios.put(url, blob, {
          signal,
          transformRequest: [(d) => d],
          // Track real bytes-out for finer progress (optional in Phase A).
        });
      } catch (err) {
        lastErr = err;
        if (signal?.aborted) throw new Error("Upload cancelled");
        // Signed URL might have expired (R2 returns 403) — re-mint and retry.
        if (err?.response?.status === 403 || err?.response?.status === 401) {
          const fresh = await _remintSignedUrl(partNumber);
          if (fresh) {
            attempt -= 1;
            continue;
          }
          // Couldn't re-mint via /status — try refreshing OUR session.
          const refreshed = await tryRefreshSession();
          if (refreshed) { attempt -= 1; continue; }
          if (typeof console !== "undefined" && console.warn) {
            console.warn(
              `[cloudUpload] auth expired on part ${partNumber}/${total_parts} — pausing.`,
            );
          }
          reportProgress("paused");
          // Stage 1 AuthExpiredError takes (uploadId, currentChunk, totalChunks)
          // where currentChunk is 0-indexed. Convert from R2's 1-indexed part.
          throw new AuthExpiredError(upload_id, partNumber - 1, total_parts);
        }
        _retryCount += 1;
        if (typeof console !== "undefined" && console.warn) {
          const status = err?.response?.status;
          const detail =
            err?.response?.data || err?.message || "network error";
          console.warn(
            `[cloudUpload] part ${partNumber}/${total_parts} attempt ${attempt + 1}/${MAX_RETRIES_PER_CHUNK} failed (status=${status ?? "n/a"}): ${typeof detail === "string" ? detail : JSON.stringify(detail).slice(0, 200)}`,
          );
        }
        const base = 1000 * Math.pow(2, attempt);
        const jitter = Math.round(base * (Math.random() * 0.5 - 0.25));
        await sleep(base + jitter);
        continue;
      }

      // PUT succeeded — capture the ETag (R2 echoes the strong ETag).
      const etag = (putRes.headers?.etag || putRes.headers?.ETag || "")
        .replace(/^"|"$/g, "");
      if (!etag) {
        // Without an ETag we can't complete multipart; rare.
        lastErr = new Error("no ETag in response");
        continue;
      }

      // Tell the backend this part is in.
      try {
        await axios.post(
          `${API}/api/uploads/cloud/${upload_id}/part-complete`,
          { part_number: partNumber, etag },
          { withCredentials: true, signal },
        );
      } catch (err) {
        // Non-fatal — backend self-heals against R2 truth on /status
        // and /complete. We still recorded local progress.
        if (typeof console !== "undefined" && console.warn) {
          console.warn(
            `[cloudUpload] part-complete POST failed for ${partNumber} — backend will self-heal from R2: ${err?.message}`,
          );
        }
      }

      received.add(partNumber);
      sentBytes += blob.size;
      _recordChunkCompletion(blob.size);
      reportProgress();
      return { ok: true };
    }

    return { ok: false, err: lastErr, paused: true };
  }

  // ── Worker pool ────────────────────────────────────────────────
  const queue = [];
  for (let i = 1; i <= total_parts; i++) {
    if (!received.has(i)) queue.push(i);
  }
  let pauseRequested = null;

  async function worker() {
    while (queue.length > 0) {
      if (signal?.aborted) throw new Error("Upload cancelled");
      if (pauseRequested) return;
      const partNumber = queue.shift();
      if (partNumber === undefined) return;
      const result = await uploadOnePart(partNumber);
      if (!result.ok && result.paused) {
        pauseRequested = { partNumber, err: result.err };
        return;
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(PARALLEL_CHUNKS, queue.length || 1) }, worker),
  );

  if (pauseRequested) {
    let lastSuccessful = -1;
    for (const idx of received) {
      const idx0 = idx - 1;   // 0-indexed for Stage 1 contract
      if (idx0 > lastSuccessful) lastSuccessful = idx0;
    }
    if (typeof console !== "undefined" && console.warn) {
      const detail =
        pauseRequested.err?.response?.data
        || pauseRequested.err?.message
        || "network error";
      console.warn(
        `[cloudUpload] pausing upload ${upload_id} after retries on part ${pauseRequested.partNumber}/${total_parts}: ${typeof detail === "string" ? detail : JSON.stringify(detail).slice(0, 200)}. ${received.size}/${total_parts} parts safe on R2.`,
      );
    }
    reportProgress("paused");
    throw new UploadPausedError(
      upload_id,
      lastSuccessful,
      total_parts,
      pauseRequested.err,
    );
  }

  // ── Complete on R2, then call purpose-specific completeUrl ─────
  for (let finalizeAttempt = 0; finalizeAttempt < 2; finalizeAttempt++) {
    reportProgress("finalizing");
    let r2Complete;
    try {
      r2Complete = await axios.post(
        `${API}/api/uploads/cloud/${upload_id}/complete`,
        {},
        { withCredentials: true, signal },
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // missing_chunks contract — same shape as Stage 1.
      if (
        err?.response?.status === 409
        && detail
        && typeof detail === "object"
        && detail.error === "missing_chunks"
        && Array.isArray(detail.missing)
        && finalizeAttempt === 0
      ) {
        if (typeof console !== "undefined" && console.warn) {
          console.warn(
            `[cloudUpload] R2 reported missing parts on finalize — re-uploading ${detail.missing.length} part(s): ${detail.missing.slice(0, 10).join(", ")}`,
          );
        }
        for (const partNumber of detail.missing) {
          received.delete(partNumber);
          const start = (partNumber - 1) * chunk_size;
          const end = Math.min(start + chunk_size, file.size);
          sentBytes -= Math.max(0, end - start);
        }
        if (sentBytes < 0) sentBytes = 0;
        reportProgress("uploading");
        for (const partNumber of detail.missing) {
          if (signal?.aborted) throw new Error("Upload cancelled");
          const result = await uploadOnePart(partNumber);
          if (!result.ok && result.paused) {
            let lastSuccessful = -1;
            for (const idx of received) {
              const idx0 = idx - 1;
              if (idx0 > lastSuccessful) lastSuccessful = idx0;
            }
            reportProgress("paused");
            throw new UploadPausedError(
              upload_id,
              lastSuccessful,
              total_parts,
              result.err,
            );
          }
        }
        continue;   // retry finalize
      }
      const text = (typeof detail === "string" && detail)
        || detail?.message
        || err?.message;
      throw new Error(`Finalization failed: ${text || "server error"}`);
    }

    // R2 multipart complete — now call the purpose-specific completeUrl
    // (e.g., /api/lesson-review/cloud-complete) with the canonical key
    // so the consumer can spawn its AI pipeline.
    let completeRes;
    try {
      completeRes = await axios.post(
        `${API}${completeUrl}`,
        {
          ...completeBody,
          upload_id,
          key: r2Complete.data.key,
          bucket: r2Complete.data.bucket,
          total_bytes: r2Complete.data.total_bytes,
          signed_get_url: r2Complete.data.signed_get_url,
        },
        { withCredentials: true, signal },
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const text = (typeof detail === "string" && detail)
        || detail?.message
        || err?.message;
      throw new Error(`Finalization failed: ${text || "server error"}`);
    }
    reportProgress("complete");
    return completeRes.data;
  }

  throw new Error("Finalization failed: max retries exceeded");
}

export function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v.toFixed(v >= 100 || u === 0 ? 0 : 1)} ${units[u]}`;
}
